import { QueryVariables, request } from '@shared/innmaxLib/services'
import message from '@shared/innmaxUI/Message'
import { compose, forEach, pathOr } from 'ramda'
import { useMutation } from 'react-query'
import Store from 'store'
import type { MenuAuth, User, UserPermission } from './auth.types'
import { useAuth } from './useAuth'

const convertData = (auth: MenuAuth) => {
  return {
    id: auth.id,
    name: auth.subName,
    url: auth.webPath,
    icon: auth.icon,
    code: auth.code,
    mainKey: auth.mainKey,
    subKey: auth.subKey,
    isSubMenu: auth.isSub ? auth.isSub : false,
    deletable: auth.authDelete,
    creatable: auth.authAdd,
    updatable: auth.authEdit,
    readable: auth.authView,
    parentId: auth.id,
    parentName: auth.name,
  } as UserPermission
}

export const setUserAuth = (auth: MenuAuth, permission: UserPermission) => {
  if (permission) {
    const child = convertData(auth)

    return {
      ...permission,
      children: [...permission.children, child],
    }
  }

  return {
    ...auth,
    url: auth.isSub ? auth.code : auth.webPath, //submenu need unique id
    isSubMenu: auth.isSub ? auth.isSub : false,
    deletable: auth.authDelete,
    creatable: auth.authAdd,
    updatable: auth.authEdit,
    readable: auth.authView,
    ...(auth.isSub
      ? {
          children: [
            { ...convertData(auth), parentId: auth.id, parentName: auth.name },
          ],
        }
      : { children: [] }),
  } as UserPermission
}

export const loginAPI = (body: User) => {
  return request('/login', {
    body,
    method: 'POST',
    displayMessage: false,
  })
}

const useSettingUser = () => {
  const { setIsAuthenticated, setUser } = useAuth()

  const setUserInfo = (response: any) => {
    let authById: { [key: string]: UserPermission } = {}
    let authByUrl: { [key: string]: UserPermission } = {}

    compose(
      forEach((x: MenuAuth) => {
        authById[x.name] = setUserAuth(x, authById[x.name])
        authByUrl[x.webPath] = setUserAuth(x, authByUrl[x.webPath])
      }),
      pathOr([], ['authDetails'])
    )(response)

    Store.set('sctc_token', response.token)
  }
  return { setUserInfo }
}

export const useLogin = (options: QueryVariables = {}) => {
  const { setUserInfo } = useSettingUser()

  const { mutate, isLoading, ...rest } = useMutation(loginAPI, {
    onError: (error: Error) => {
      console.error(error)
    },
    onSuccess: (response: any) => {
      if (!response) {
        return
      }

      if (response?.errorMessage) {
        message({ content: response?.errorMessage, type: 'error' })
        return
      }
      if (response.token) {
        setUserInfo(response)
      }
      options.onSuccess && options.onSuccess(response)
    },
  })

  return {
    ...rest,
    mutate,
    loading: isLoading,
    login: mutate,
  }
}

export const useTwoFactorAuth = (options: QueryVariables = {}) => {
  const { setUserInfo } = useSettingUser()

  const { mutate, isLoading, ...rest } = useMutation(
    (body: { code: string; loginId: string }) => {
      return request('/login/verify-2fa', {
        body,
        method: 'POST',
        displayMessage: false,
      })
    },
    {
      onError: (error: Error) => {
        console.error(error)
      },
      onSuccess: (response: any) => {
        if (!response) {
          return
        }

        if (response?.errorMessage) {
          message({ content: response?.errorMessage, type: 'error' })
          return
        }

        setUserInfo(response)

        options.onSuccess && options.onSuccess(response)
      },
    }
  )

  return {
    ...rest,
    mutate,
    loading: isLoading,
    login: mutate,
  }
}
